/** [lon, lat] — GeoJSON order, kept consistently everywhere in the app. */
export type LngLat = [number, number];

const EARTH_RADIUS_M = 6_371_008.8;
const RAD = Math.PI / 180;

/** Great-circle distance in metres. */
export function distanceMeters(a: LngLat, b: LngLat): number {
  const dLat = (b[1] - a[1]) * RAD;
  const dLon = (b[0] - a[0]) * RAD;
  const lat1 = a[1] * RAD;
  const lat2 = b[1] * RAD;
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

/**
 * A circle as a GeoJSON polygon, for drawing the radius tool. Steps are spaced
 * in true bearing so the ring stays circular on the ground (and therefore looks
 * circular in Web Mercator at these latitudes) rather than in raw degrees.
 */
export function circlePolygon(center: LngLat, radiusM: number, steps = 96): GeoJSON.Polygon {
  const [lon, lat] = center;
  const latR = lat * RAD;
  const dLat = radiusM / EARTH_RADIUS_M / RAD;
  const dLon = dLat / Math.max(Math.cos(latR), 1e-6);
  const ring: GeoJSON.Position[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    ring.push([lon + dLon * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return { type: "Polygon", coordinates: [ring] };
}

/** The point `radiusM` due east of `center` — where the drag handle rides. */
export function eastOf(center: LngLat, radiusM: number): LngLat {
  const dLon = radiusM / EARTH_RADIUS_M / RAD / Math.max(Math.cos(center[1] * RAD), 1e-6);
  return [center[0] + dLon, center[1]];
}

/** [minLon, minLat, maxLon, maxLat] */
export type BBox = [number, number, number, number];

export function multiPolygonBBox(mp: GeoJSON.MultiPolygon): BBox {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;
  for (const poly of mp.coordinates) {
    for (const [lon, lat] of poly[0]) {
      if (lon < minLon) minLon = lon;
      if (lat < minLat) minLat = lat;
      if (lon > maxLon) maxLon = lon;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return [minLon, minLat, maxLon, maxLat];
}

export function unionBBox(boxes: BBox[]): BBox | null {
  if (boxes.length === 0) return null;
  return boxes.reduce<BBox>(
    (acc, b) => [
      Math.min(acc[0], b[0]),
      Math.min(acc[1], b[1]),
      Math.max(acc[2], b[2]),
      Math.max(acc[3], b[3]),
    ],
    [Infinity, Infinity, -Infinity, -Infinity],
  );
}

/**
 * Even-odd ray casting against every ring of a polygon at once. Counting
 * crossings across outer rings and holes together gives the right answer for
 * holes for free: a point inside a hole crosses the outer ring and the hole ring,
 * landing on an even count.
 */
function pointInRings(lon: number, lat: number, rings: GeoJSON.Position[][]): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
  }
  return inside;
}

export function pointInMultiPolygon(lon: number, lat: number, mp: GeoJSON.MultiPolygon): boolean {
  for (const poly of mp.coordinates) {
    if (pointInRings(lon, lat, poly)) return true;
  }
  return false;
}
